import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, pairwise } from 'rxjs/operators';
import { Subject } from 'rxjs';

export interface RestoreScrollListener {
  tag: string;
  from: RegExp;
  to: string;
  scrollTo: ScrollToOptions;
}

export interface RestoreScrollEvent {
  tag: string;
  scrollTo: ScrollToOptions;
}

@Injectable({ providedIn: 'root' })
export class ScrollPositionService {
  private listeners: { [tag: string]: RestoreScrollListener } = {};
  private readonly events = new Subject<RestoreScrollEvent>();
  readonly events$ = this.events.asObservable();
  constructor(private readonly router: Router) {
    this.setup();
  }

  /**
   * Register a listener that will cause an event to be emitted when a user visits the current
   * page from another page. This should be called directly after a NavigationStart event is emitted
   * from the router.
   *
   * You can listen for events by subscribing to the events$ property and filtering by the tag
   * you provide.
   *
   * @param tag A custom value to filter on
   * @param from  Pattern matched against the URL the user is coming from
   * @param scrollTo  The position to scroll to
   */
  addRestoreScrollListener(tag: string, from: RegExp, scrollTo: ScrollToOptions) {
    const to = this.router.currentNavigation().previousNavigation.finalUrl.toString();
    this.listeners[tag] = { tag, from, scrollTo, to };
  }

  removeRestoreScrollListener(tag: string) {
    delete this.listeners[tag];
  }

  clearListeners() {
    this.listeners = {};
  }

  getListeners() {
    return this.listeners;
  }

  setup() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        pairwise<NavigationEnd>()
      )
      .subscribe(([fromEvent, toEvent]) => {
        const listeners = Object.keys(this.listeners).map(k => this.listeners[k]);
        listeners.forEach(listener => {
          if (listener.from.test(fromEvent.urlAfterRedirects) && toEvent.urlAfterRedirects === listener.to) {
            const { tag, scrollTo } = listener;
            this.events.next({ tag, scrollTo });
            delete this.listeners[tag];
          }
        });
      });
  }
}
